import React, { useState, useMemo, useRef } from "react";
import "./RequestPaperWorkModal.css";
import { MdClose } from "react-icons/md";
import { DocumentViewer } from "../../DocumentViewer/DocumentViewer";
import { useSelector } from "react-redux";
import { parseDocumentUrls } from "../../DocumentViewer/utils";
import CancelBtn from "../../CustomButtons/CancelBtn/CancelBtn";

interface Recipient { name: string; email: string; }
interface InvoiceRow { date: string; reference: string; type: string; totalAmount: string; }

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const DUMMY_ROWS: InvoiceRow[] = [
  { date: "02/07/2026", reference: "130663", type: "Invoice", totalAmount: "58.20" },
  { date: "05/07/2026", reference: "30231391", type: "Invoice", totalAmount: "4,204.22" },
  { date: "02/07/2026", reference: "132657", type: "Invoice", totalAmount: "1,908.11" },
  { date: "12/07/2026", reference: "130677", type: "Invoice", totalAmount: "987.00" },
  { date: "09/07/2026", reference: "132661", type: "Invoice", totalAmount: "985.49" },
];

const INITIAL_SENDER = "";
const INITIAL_RECIPIENTS: Recipient[] = [{ name: "", email: "" }];

const RequestPaperWorkModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const selectedInvoiceData = useSelector((state: any) => state.selectedInvoiceData.selectedInvoiceData);
  const urls = useMemo(() => parseDocumentUrls(selectedInvoiceData?.documentUrl), [selectedInvoiceData?.documentUrl]);

  const [senderName, setSenderName] = useState(INITIAL_SENDER);
  const [recipients, setRecipients] = useState<Recipient[]>(INITIAL_RECIPIENTS);
  const [rows, setRows] = useState<InvoiceRow[]>(DUMMY_ROWS);
  const [showError, setShowError] = useState(false);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const savedRef = useRef({ senderName: INITIAL_SENDER, recipients: INITIAL_RECIPIENTS, rows: DUMMY_ROWS });

  const isDirty =
    JSON.stringify({ senderName, recipients, rows }) !== JSON.stringify(savedRef.current);

  if (!isOpen) return null;

  const addRecipient = () => {
    setRecipients((prev) => [...prev, { name: "", email: "" }]);
    setTimeout(() => {
      if (rightPanelRef.current) {
        rightPanelRef.current.scrollTop = rightPanelRef.current.scrollHeight;
      }
    }, 50);
  };

  const removeRecipient = (i: number) =>
    setRecipients((prev) => prev.filter((_, idx) => idx !== i));

  const updateRecipient = (i: number, field: keyof Recipient, value: string) =>
    setRecipients((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const addRow = () =>
    setRows((prev) => [...prev, { date: "", reference: "", type: "Invoice", totalAmount: "" }]);

  const updateRow = (i: number, field: keyof InvoiceRow, value: string) =>
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const deleteRow = (i: number) =>
    setRows((prev) => prev.filter((_, idx) => idx !== i));

  const handleSend = () => {
    const hasEmail = recipients.some((r) => r.email.trim() !== "");
    if (!hasEmail) { setShowError(true); return; }
    setShowError(false);
    onClose();
  };

  return (
    <div className="rpw-overlay ">
      <div className="rpw-modal">
        
        <div className="rpw-header">
          <h3>Request Paper Work</h3>
          <CancelBtn CancelBtnClick={onClose} />
        </div>

       
        <div className="rpw-body">
         
          <div className="rpw-left">
            <DocumentViewer urls={urls} isToolbarActive={false} />
          </div>

         
          <div className="rpw-right" ref={rightPanelRef}>
            <p className="rpw-info">
              An email will be sent to the recipient with a list of the missing invoices or credit notes.
            </p>

            
            <div className="rpw-field">
              <div className="rpw-field-row">
                <label className="rpw-label">Sender name <span className="rpw-required">*</span></label>
                <input
                  className="rpw-input"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                />
              </div>
            </div>

           
            <div className="rpw-field">
              <div className="rpw-recipients-wrap">
                {recipients.map((r, i) => (
                  <div key={i} className="rpw-recipient-field-row">
                    <label className="rpw-label">{i === 0 ? <><span>Recipient</span> <span className="rpw-required">*</span></> : "Recipient"}</label>
                    <div className="rpw-recipient-inputs">
                      <input
                        className="rpw-input"
                        placeholder="Name"
                        value={r.name}
                        onChange={(e) => updateRecipient(i, "name", e.target.value)}
                      />
                      <input
                        className="rpw-input"
                        placeholder="Email"
                        value={r.email}
                        onChange={(e) => { updateRecipient(i, "email", e.target.value); setShowError(false); }}
                      />
                      {i === 0 ? (
                        <button className="rpw-add-recipient" onClick={addRecipient} title="Add recipient">+</button>
                      ) : (
                        <button className="rpw-remove-recipient" onClick={() => removeRecipient(i)} title="Remove"><MdClose /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {showError && (
                <p className="rpw-error">Please add at least one recipient email address.</p>
              )}
            </div>

          
            <div className="rpw-table-wrap">
              <div className="rpw-table-header">
                <span>Date</span>
                <span>Reference</span>
                <span>Type</span>
                <span>Total amount</span>
                <span></span>
              </div>
              <div className="rpw-rows-list">
                {rows.map((row, i) => (
                  <div key={i} className="rpw-row-card">
                    <input className="rpw-table-input" placeholder="dd/mm/yyyy" value={row.date} onChange={(e) => updateRow(i, "date", e.target.value)} />
                    <input className="rpw-table-input" placeholder="Reference" value={row.reference} onChange={(e) => updateRow(i, "reference", e.target.value)} />
                    <select className="rpw-table-select" value={row.type} onChange={(e) => updateRow(i, "type", e.target.value)}>
                      <option>Invoice</option>
                      <option>Credit Note</option>
                    </select>
                    <input className="rpw-table-input" placeholder="0.00" value={row.totalAmount} onChange={(e) => updateRow(i, "totalAmount", e.target.value)} />
                    <button className="rpw-row-delete" onClick={() => deleteRow(i)}><MdClose /></button>
                  </div>
                ))}
              </div>
            </div>

            <button className="rpw-add-row" onClick={addRow}>Add</button>
          </div>
        </div>

      
        <div className="rpw-footer">
          <button className="rpw-btn rpw-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="rpw-btn rpw-btn-export"
            disabled={!isDirty}
            style={{ opacity: isDirty ? 1 : 0.5, cursor: isDirty ? "pointer" : "not-allowed" }}
          >Export</button>
          <button
            className="rpw-btn rpw-btn-send"
            onClick={handleSend}
            disabled={!isDirty}
            style={{ opacity: isDirty ? 1 : 0.5, cursor: isDirty ? "pointer" : "not-allowed" }}
          >Send</button>
        </div>
      </div>
    </div>
  );
};

export default RequestPaperWorkModal;
